/**
 * Which social post, profile or campaign sent this visitor, from the `utm_*` tags on
 * the link they clicked.
 *
 * This exists because attribution had a hole in it. `site-referral.ts` answers "which
 * client site sent them" for the footer credit on the sites we build, but nothing read
 * the tags a social link carries, so an enquiry that started life as an Instagram post
 * and one that started on Google arrived in Lead Engine looking identical. You cannot
 * decide what to post more of when every lead says the same thing.
 *
 * SAME PRIVACY RULE AS site-referral.ts, and for the same reason: nothing is written to
 * the visitor's device. No cookie, no localStorage, no sessionStorage, no third-party
 * pixel. The tag lives in a module variable for the life of the tab. Our privacy policy
 * promises this site works without non-essential tracking cookies, and that promise is
 * worth more than perfect attribution. A visitor who does a full page reload is simply
 * no longer attributed, and that is the right trade.
 *
 * The practical consequence, worth knowing before anyone reports it as a bug: these
 * numbers UNDER-count. They are a floor, not a total. Treat them as "at least this many
 * came from Instagram", never as an exact figure.
 */

/** Longest tag we will accept. Ours are short slugs like "instagram" or "audit-teardown". */
const MAX_LENGTH = 40;

export type CampaignTag = {
  /** Where they came from: instagram, linkedin, facebook… */
  source: string;
  /** How they got here within that platform: bio, post, story, dm… */
  medium: string | null;
  /** Which specific post or campaign, when we bothered to tag one. */
  campaign: string | null;
};

export type AdClickIds = {
  gclid: string | null;
  msclkid: string | null;
  fbclid: string | null;
};

const EMPTY_CLICK_IDS: AdClickIds = { gclid: null, msclkid: null, fbclid: null };

/** Click IDs are opaque provider values, so allow only their documented URL-safe shape. */
export function cleanAdClickId(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim().slice(0, 255);
  return /^[A-Za-z0-9._~-]+$/.test(value) ? value : null;
}

export function readAdClickIds(params: URLSearchParams): AdClickIds {
  return {
    gclid: cleanAdClickId(params.get("gclid")),
    msclkid: cleanAdClickId(params.get("msclkid")),
    fbclid: cleanAdClickId(params.get("fbclid")),
  };
}

export function hasAdClickId(ids: AdClickIds): boolean {
  return Boolean(ids.gclid || ids.msclkid || ids.fbclid);
}

/** Provider name only. Raw click IDs never enter aggregate analytics event properties. */
export function adClickSource(ids: AdClickIds): string | null {
  if (ids.gclid) return "Google Ads";
  if (ids.msclkid) return "Microsoft Ads";
  if (ids.fbclid) return "Meta";
  return null;
}

/** Full references are kept only with a submitted lead, for later conversion matching. */
export function adClickReference(ids: AdClickIds): string | null {
  const values = [
    ids.gclid ? `gclid=${ids.gclid}` : null,
    ids.msclkid ? `msclkid=${ids.msclkid}` : null,
    ids.fbclid ? `fbclid=${ids.fbclid}` : null,
  ].filter(Boolean);
  return values.length ? values.join(", ") : null;
}

/**
 * Reduce a raw `utm_*` value to a safe slug, or null.
 *
 * These values reach an email and a lead record, so they are allow-listed rather than
 * escaped: lowercase letters, digits and hyphens, nothing else. Anything a stranger
 * puts in the query string that does not look like one of our tags is dropped. This is
 * the same rule as `cleanSiteRef`, deliberately, because both end up in the same places.
 */
export function cleanCampaignTag(raw: string | null | undefined): string | null {
  if (typeof raw !== "string") return null;
  const value = raw.trim().toLowerCase().slice(0, MAX_LENGTH);
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) ? value : null;
}

/**
 * Plain-English names for the platforms we actually post on, so a notification reads
 * "came from Instagram" rather than "came from instagram". An unknown source falls back
 * to itself, so a platform we add later still attributes correctly, it just reads as
 * its slug until someone adds a line here.
 */
const SOURCE_NAMES: Record<string, string> = {
  instagram: "Instagram",
  linkedin: "LinkedIn",
  facebook: "Facebook",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  twitter: "X",
  threads: "Threads",
  reddit: "Reddit",
  /* Tom's own LinkedIn profile, kept apart from the Ownly company page ("linkedin") so the two
     can be compared. Lead Engine has the same line in its copy of this table. */
  "linkedin-tom": "Tom's LinkedIn",
  email: "an email",
  newsletter: "the newsletter",
};

/** How they got to us within that platform, in words a person would say. */
const MEDIUM_NAMES: Record<string, string> = {
  bio: "bio link",
  profile: "profile link",
  post: "a post",
  reel: "a reel",
  story: "a story",
  video: "a video",
  comment: "a comment",
  dm: "a DM",
  ad: "an ad",
  /* The free guide's follow-up emails link to the briefing and to /book with this. */
  guide: "guide follow-up",
};

export function campaignSourceName(source: string): string {
  return SOURCE_NAMES[source] ?? source;
}

/**
 * One readable line for the CRM's "referral source" field and the notification email.
 *
 * Reads as a sentence fragment on purpose, because it gets slotted after "Came from: "
 * and into subject lines. "Instagram (bio link)" beats "utm_source=instagram&utm_medium=bio"
 * for the person who has to decide whether to ring them.
 */
export function campaignLabel(tag: CampaignTag): string {
  const source = campaignSourceName(tag.source);
  const medium = tag.medium ? (MEDIUM_NAMES[tag.medium] ?? tag.medium) : null;
  const base = medium ? `${source} (${medium})` : source;
  return tag.campaign ? `${base} · ${tag.campaign}` : base;
}

/**
 * Pull a campaign tag out of a query string.
 *
 * `utm_source` is the one that matters: without it we do not know where they came from,
 * so a link carrying only `utm_campaign` gives us nothing and returns null rather than a
 * half-tag that would read as "came from null" in an email.
 */
export function readCampaignTag(params: URLSearchParams): CampaignTag | null {
  const source = cleanCampaignTag(params.get("utm_source"));
  if (!source) return reelTagFromSrc(params.get("src"));
  return {
    source,
    medium: cleanCampaignTag(params.get("utm_medium")),
    campaign: cleanCampaignTag(params.get("utm_campaign")),
  };
}

/**
 * The short form of a reel tag, `?src=reel-v08`, for a link somebody types by hand. Reads exactly
 * as the tags the /v/<nn> redirect sets. Only the reel shape is accepted: `src` is not a general
 * back door for arbitrary campaigns.
 */
function reelTagFromSrc(raw: string | null): CampaignTag | null {
  const value = cleanCampaignTag(raw);
  if (!value || !/^reel-v\d{2}$/.test(value)) return null;
  return { source: "instagram", medium: "reel", campaign: value };
}

/**
 * Rebuild a tag from the three fields a form posts to us, dropping anything that is not
 * a tag we would have issued. Used on the server, where every value is attacker-supplied.
 */
export function campaignFromFields(fields: {
  utmSource?: unknown;
  utmMedium?: unknown;
  utmCampaign?: unknown;
}): CampaignTag | null {
  const source = cleanCampaignTag(
    typeof fields.utmSource === "string" ? fields.utmSource : null,
  );
  if (!source) return null;
  return {
    source,
    medium: cleanCampaignTag(
      typeof fields.utmMedium === "string" ? fields.utmMedium : null,
    ),
    campaign: cleanCampaignTag(
      typeof fields.utmCampaign === "string" ? fields.utmCampaign : null,
    ),
  };
}

/** True when two tags say the same thing, so the tracker does not re-fire on every nav. */
export function sameCampaign(a: CampaignTag | null, b: CampaignTag | null): boolean {
  if (!a || !b) return a === b;
  return a.source === b.source && a.medium === b.medium && a.campaign === b.campaign;
}

/* ------------------------------------------------------------------ in-memory tab state */

let campaignForThisTab: CampaignTag | null = null;
let adClickIdsForThisTab: AdClickIds = EMPTY_CLICK_IDS;

/**
 * Remembers the tag for as long as this tab is open. Memory only, never the device.
 *
 * FIRST TOUCH WINS. A visitor who arrives from Tom's LinkedIn and then taps an Instagram link to
 * us in the same tab is still a LinkedIn visitor: that is the link that brought them in, and it
 * is the answer the booking alert and Lead Engine need. The later tag is kept separately as
 * `latestCampaign()` for anyone who wants it, but nothing overwrites the first.
 */
export function rememberCampaign(tag: CampaignTag): void {
  latestCampaignForThisTab = tag;
  if (!campaignForThisTab) campaignForThisTab = tag;
}

let latestCampaignForThisTab: CampaignTag | null = null;

/** The most recent tag seen in this tab, which may differ from the first. */
export function latestCampaign(): CampaignTag | null {
  return latestCampaignForThisTab;
}

/** The tag this visitor arrived with, if they are still in the same tab. */
export function currentCampaign(): CampaignTag | null {
  return campaignForThisTab;
}

export function rememberAdClickIds(ids: AdClickIds): void {
  adClickIdsForThisTab = ids;
}

export function currentAdClickIds(): AdClickIds {
  return adClickIdsForThisTab;
}

export function adClickIdsFromFields(fields: {
  gclid?: unknown;
  msclkid?: unknown;
  fbclid?: unknown;
}): AdClickIds {
  return {
    gclid: cleanAdClickId(typeof fields.gclid === "string" ? fields.gclid : null),
    msclkid: cleanAdClickId(typeof fields.msclkid === "string" ? fields.msclkid : null),
    fbclid: cleanAdClickId(typeof fields.fbclid === "string" ? fields.fbclid : null),
  };
}

/**
 * The attribution fields a form posts alongside its own answers.
 *
 * Flat strings rather than a nested object because Lead Engine's intake reads a fixed
 * alias list and silently drops anything shaped differently — the trap that once meant
 * every quiz answer arrived blank. Our own routes fold these into `referralSource` and
 * `notes`, which Lead Engine does read, so no change is needed at that end.
 */
export function campaignFields(): {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  gclid?: string;
  msclkid?: string;
  fbclid?: string;
} {
  const tag = currentCampaign();
  const clickIds = currentAdClickIds();
  return {
    ...(tag ? { utmSource: tag.source } : {}),
    ...(tag?.medium ? { utmMedium: tag.medium } : {}),
    ...(tag?.campaign ? { utmCampaign: tag.campaign } : {}),
    ...(clickIds.gclid ? { gclid: clickIds.gclid } : {}),
    ...(clickIds.msclkid ? { msclkid: clickIds.msclkid } : {}),
    ...(clickIds.fbclid ? { fbclid: clickIds.fbclid } : {}),
  };
}
